import type { EventView } from '@goaltree/backend/convex/events'
import { eventPhase, eventRelative } from '@goaltree/backend/convex/lib/events'
import { cn } from '@goaltree/ui/lib/utils'
import { CalendarDays, Check, Lock, MapPin, Navigation } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { durationLabel, hhmm, relativeLabel, travelLabel } from '@/lib/events'
import { useEventSheet } from '@/store/event-sheet.store'

// Events read as context, not to-dos: neutral grey, a calendar icon where a
// task's checkbox would be, time on the left, and "Done" once they're over.

export type EventLike = Pick<
  EventView,
  | '_id'
  | 'title'
  | 'startsAt'
  | 'endsAt'
  | 'allDay'
  | 'durationMinutes'
  | 'location'
  | 'goal'
  | 'travel'
>

const SOON_MS = 3 * 3_600_000
// Time column + card, shared by the Today strip rows below.
const STRIP_GRID = 'grid grid-cols-[46px_1fr] items-stretch'

// The goal's category colour when the event belongs to one, else the accent.
function useAccentStyle(event: EventLike) {
  const fill = useCategoryFill()
  const color = event.goal ? CATEGORY_COLORS[event.goal.category.toLowerCase()] : undefined
  return { '--event-accent': color ? fill(color) : 'var(--primary)' } as CSSProperties
}

function EventMeta({
  event,
  now,
  showTravel = true,
}: {
  event: EventLike
  now: number
  showTravel?: boolean
}) {
  const { t, i18n } = useTranslation()
  const fill = useCategoryFill()
  const phase = eventPhase(event, now)
  const rel = eventRelative(event, now)
  const soon = phase === 'now' || (phase === 'upcoming' && event.startsAt - now < SOON_MS)
  const color = event.goal ? CATEGORY_COLORS[event.goal.category.toLowerCase()] : undefined
  return (
    <div className="mt-1 flex min-w-0 items-center gap-x-2.5 overflow-hidden font-mono text-[11px] text-muted-foreground">
      {event.location && (
        <span className="flex min-w-0 max-w-[12rem] shrink items-center gap-1 leading-none">
          <MapPin className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{event.location}</span>
        </span>
      )}
      {showTravel && event.travel && phase === 'upcoming' && (
        <span className="flex shrink-0 items-center gap-1 leading-none whitespace-nowrap">
          <Navigation className="h-2.5 w-2.5 shrink-0" />
          {travelLabel(event.travel, t, i18n.language)}
        </span>
      )}
      {phase === 'done' ? (
        <span className="flex shrink-0 items-center gap-0.5 leading-none text-muted-foreground/60">
          <Check className="h-2.5 w-2.5" strokeWidth={3} />
          {t('events.done')}
        </span>
      ) : (
        !event.allDay && (
          <span
            className={cn(
              'shrink-0 leading-none whitespace-nowrap',
              soon && 'font-semibold text-primary-text',
            )}
          >
            {relativeLabel(rel, t)}
          </span>
        )
      )}
      {event.goal && (
        <span className="flex min-w-0 items-center gap-1 leading-none">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: color ? fill(color) : 'var(--muted-foreground)' }}
          />
          <span className="truncate">{event.goal.title}</span>
        </span>
      )}
    </div>
  )
}

function EventCard({
  event,
  now,
  next,
  truncate,
  className,
}: {
  event: EventLike
  now: number
  next?: boolean
  truncate?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const open = useEventSheet((s) => s.open)
  const accent = useAccentStyle(event)
  const past = eventPhase(event, now) === 'done'
  const time = event.allDay ? t('events.allDay') : `${hhmm(event.startsAt)}–${hhmm(event.endsAt)}`
  return (
    <button
      type="button"
      onClick={() => open(event._id)}
      aria-label={t('events.openEvent', { title: event.title, time })}
      style={accent}
      className={cn(
        'relative mb-1.5 flex w-full min-w-0 cursor-pointer items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors',
        'before:absolute before:top-2.5 before:bottom-2.5 before:-left-px before:w-[3px] before:rounded-r-[3px] before:bg-(--event-accent)',
        past
          ? 'border-dashed border-border/70 bg-transparent before:opacity-25 hover:bg-muted/40'
          : next
            ? 'border-primary/40 bg-card shadow-sm before:opacity-100 hover:bg-muted/40'
            : 'border-border/70 bg-card before:opacity-75 hover:bg-muted/40',
        className,
      )}
    >
      <CalendarDays
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          past ? 'text-muted-foreground/60' : 'text-foreground',
        )}
        strokeWidth={2}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block text-[14.5px] font-semibold leading-snug',
            truncate && 'truncate',
            past && 'text-muted-foreground line-through decoration-muted-foreground/50',
          )}
        >
          {event.title}
        </span>
        <EventMeta event={event} now={now} />
      </span>
    </button>
  )
}

// Today's schedule row: start time and length on the left. `next` marks the
// event in progress or, failing that, the next to start.
export function EventStripRow({
  event,
  now,
  next,
}: {
  event: EventLike
  now: number
  next?: boolean
}) {
  const { t } = useTranslation()
  const past = eventPhase(event, now) === 'done'
  return (
    <div className={STRIP_GRID}>
      <span
        className={cn(
          'pt-3 font-mono text-[11.5px] leading-tight tabular-nums',
          past
            ? 'text-muted-foreground/60'
            : next
              ? 'font-semibold text-foreground'
              : 'text-muted-foreground',
        )}
      >
        {hhmm(event.startsAt)}
        {event.durationMinutes != null && (
          <small className="block text-[10px] text-muted-foreground/60">
            {durationLabel(event.durationMinutes, t)}
          </small>
        )}
      </span>
      <EventCard event={event} now={now} next={next} truncate />
    </div>
  )
}

// A Google Calendar busy block: hatched, locked, read-only.
export function BusyStripRow({ start, end, past }: { start: number; end: number; past?: boolean }) {
  const { t } = useTranslation()
  return (
    <div className={cn(STRIP_GRID, past && 'opacity-60')}>
      <span className="pt-3 font-mono text-[11.5px] leading-tight tabular-nums text-muted-foreground/70">
        {hhmm(start)}
        <small className="block text-[10px] text-muted-foreground/50">
          {durationLabel(Math.max(5, Math.round((end - start) / 60_000)), t)}
        </small>
      </span>
      <div
        role="note"
        aria-label={t('events.busyAria', { from: hhmm(start), to: hhmm(end) })}
        className="mb-1.5 flex items-center gap-2.5 rounded-xl border border-dashed border-border/70 bg-[repeating-linear-gradient(135deg,rgba(0,0,0,.05)_0_6px,transparent_6px_12px)] px-3 py-2.5 text-[13px] text-muted-foreground dark:bg-[repeating-linear-gradient(135deg,rgba(255,255,255,.05)_0_6px,transparent_6px_12px)]"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 truncate">{t('events.busyGoogle')}</span>
        <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/70">
          {hhmm(start)}–{hhmm(end)}
        </span>
      </div>
    </div>
  )
}

export function NowLine({ now }: { now: number }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(STRIP_GRID, 'my-1 items-center')}
      role="separator"
      aria-label={t('events.nowAria', { time: hhmm(now) })}
    >
      <span className="font-mono text-[10.5px] font-bold tabular-nums text-primary-text">
        {hhmm(now)}
      </span>
      <span className="relative h-0.5 rounded-full bg-primary before:absolute before:-top-[3px] before:-left-1 before:h-2 before:w-2 before:rounded-full before:bg-primary" />
    </div>
  )
}

// All-day events sit above the timed ones in a tinted band.
export function AllDayBand({ events, now }: { events: EventLike[]; now: number }) {
  const { t } = useTranslation()
  const open = useEventSheet((s) => s.open)
  if (!events.length) return null
  return (
    <div className="mb-2 flex flex-col gap-1">
      {events.map((e) => (
        <button
          key={e._id}
          type="button"
          onClick={() => open(e._id)}
          aria-label={t('events.openEvent', { title: e.title, time: t('events.allDay') })}
          className="flex w-full cursor-pointer items-start gap-2.5 rounded-xl bg-foreground/[0.06] px-3 py-2.5 text-left transition-colors hover:bg-foreground/[0.09] dark:bg-white/[0.09] dark:hover:bg-white/[0.12]"
        >
          <span className="w-[34px] shrink-0 pt-0.5 font-mono text-[10px] font-bold uppercase leading-tight tracking-wide text-muted-foreground">
            {t('events.allDayShort')}
          </span>
          <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-semibold leading-snug">{e.title}</span>
            <EventMeta event={e} now={now} showTravel={false} />
          </span>
        </button>
      ))}
    </div>
  )
}

// Compact list row (Upcoming, Coming up): time, a dark bar, title and place.
export function EventListRow({
  event,
  now,
  trailing,
  dayPrefix,
}: {
  event: EventLike
  now: number
  trailing?: ReactNode
  // "Tue" in the Coming up panel.
  dayPrefix?: string
}) {
  const { t } = useTranslation()
  const open = useEventSheet((s) => s.open)
  const accent = useAccentStyle(event)
  const past = eventPhase(event, now) === 'done'
  const time = event.allDay ? t('events.allDay') : hhmm(event.startsAt)
  const timeClass = cn(
    'font-mono text-[11.5px] tabular-nums',
    event.allDay && 'text-[10px] uppercase tracking-wide',
    past ? 'text-muted-foreground/60' : 'text-muted-foreground',
  )
  return (
    <button
      type="button"
      onClick={() => open(event._id)}
      aria-label={t('events.openEvent', { title: event.title, time })}
      style={accent}
      className="flex min-h-12 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/50"
    >
      {dayPrefix ? (
        // Day over time, so the column lines up with the Today schedule's.
        <span className="flex w-9 shrink-0 flex-col leading-tight">
          <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            {dayPrefix}
          </span>
          <span className={cn(timeClass, 'truncate')}>{time}</span>
        </span>
      ) : (
        <span className={cn(timeClass, 'w-12 shrink-0')}>{time}</span>
      )}
      <span
        className={cn(
          'h-8 w-[3px] shrink-0 rounded-full bg-(--event-accent)',
          past ? 'opacity-25' : 'opacity-75',
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm font-semibold',
            past && 'text-muted-foreground line-through decoration-muted-foreground/50',
          )}
        >
          {event.title}
        </span>
        {event.location && (
          <span className="flex min-w-0 items-center gap-1 font-mono text-[11px] text-muted-foreground">
            <MapPin className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{event.location}</span>
          </span>
        )}
      </span>
      {trailing}
    </button>
  )
}

// Read-only reference card (coach chat): the E2 card without the time column.
export function EventRefCard({ event, now }: { event: EventLike; now: number }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col">
      <span className="mb-1 font-mono text-[10.5px] text-muted-foreground">
        {event.allDay ? t('events.allDay') : `${hhmm(event.startsAt)}–${hhmm(event.endsAt)}`}
      </span>
      <EventCard event={event} now={now} className="mb-0" />
    </div>
  )
}
